/**
 * M11 — sync `_nb_bundle_qualifier` on the Ajax theme cart after every change.
 */

import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import {
  BUNDLE_QUALIFIER_ATTRIBUTE,
  BUNDLE_RULES,
  resolveBundleQualifier,
} from '@theme/bundle-qualifier-config';

const SOURCE = 'bundle-cart-qualifier';
const FOLLOW_UP_MS = 300;
const MAX_ALIGN_ATTEMPTS = 4;
const DISCOUNT_LAG_MS = 400;

const BUNDLE_DISCOUNT_TITLES = new Set(BUNDLE_RULES.map((rule) => rule.title));

/** @type {ReturnType<typeof setTimeout> | null} */
let followUpTimer = null;
let inFlight = false;
let pendingSync = false;
/** @type {{ items?: unknown[], attributes?: Record<string, string> } | null} */
let pendingCartSnapshot = null;
/** @type {Promise<void>} */
let inFlightDone = Promise.resolve();

/**
 * @param {number} ms
 * @returns {Promise<void>}
 */
function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * @param {string} url
 * @returns {string}
 */
function cartUpdateUrl(url) {
  return url.endsWith('.js') ? url : `${url}.js`;
}

/**
 * Section IDs for live cart drawers / cart pages so attribute updates can re-render discounts.
 * @returns {string[]}
 */
function collectCartSectionIds() {
  /** @type {string[]} */
  const ids = [];
  for (const el of document.querySelectorAll('cart-items-component[data-section-id]')) {
    if (!(el instanceof HTMLElement)) continue;
    const sectionId = el.dataset.sectionId;
    if (sectionId) ids.push(sectionId);
  }
  return [...new Set(ids)];
}

/**
 * @param {unknown} app
 * @returns {boolean}
 */
function isBundleDiscountApp(app) {
  if (!app || typeof app !== 'object') return false;
  const title = /** @type {{ title?: string }} */ (app).title;
  return Boolean(title && BUNDLE_DISCOUNT_TITLES.has(title));
}

/**
 * @returns {Promise<{
 *   items?: unknown[],
 *   attributes?: Record<string, string>,
 *   item_count?: number,
 *   cart_level_discount_applications?: unknown[],
 * }>}
 */
async function fetchFreshCart() {
  const response = await fetch('/cart.js', {
    credentials: 'same-origin',
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(`cart read failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Force cart drawers/pages to re-fetch section HTML (aborts in-flight undiscounded morphs).
 * Intentionally omits `sections` so `#handleCartUpdate` uses sectionRenderer.renderSection.
 * @param {{ items?: unknown[], attributes?: Record<string, string>, item_count?: number }} cart
 */
function notifyCartSectionsRefreshed(cart) {
  document.dispatchEvent(
    new CartUpdateEvent(cart, SOURCE, {
      source: SOURCE,
      itemCount: cart.item_count,
    })
  );
}

/**
 * Re-render cart UI after qualifier sync.
 * Waits briefly when PowerX apps lag behind attribute set OR clear.
 * @returns {Promise<void>}
 */
async function refreshCartDiscountUi() {
  let cart = await fetchFreshCart();
  const qualifier = cart.attributes?.[BUNDLE_QUALIFIER_ATTRIBUTE] ?? '';
  const apps = cart.cart_level_discount_applications || [];
  const bundleApps = apps.filter(isBundleDiscountApp);

  // Set lag: qualifier written, PowerX not in cart yet.
  // Clear lag: qualifier cleared, PowerX line still applied.
  const discountPending =
    (Boolean(qualifier) && bundleApps.length === 0) || (!qualifier && bundleApps.length > 0);

  if (discountPending) {
    await delay(DISCOUNT_LAG_MS);
    cart = await fetchFreshCart();
  }

  notifyCartSectionsRefreshed(cart);
}

/**
 * @param {{ items?: unknown[], attributes?: Record<string, string> }} cart
 * @returns {Promise<{ items?: unknown[], attributes?: Record<string, string>, item_count?: number } | null>}
 */
async function applyBundleQualifier(cart) {
  const next = resolveBundleQualifier(cart.items || []);
  const current = cart.attributes?.[BUNDLE_QUALIFIER_ATTRIBUTE] ?? '';

  if (next === current) return cart;

  const sectionIds = collectCartSectionIds();
  // Empty string clears the attribute so PowerX Cart Has Attribute stops matching.
  /** @type {Record<string, unknown>} */
  const body = {
    attributes: { [BUNDLE_QUALIFIER_ATTRIBUTE]: next },
  };
  if (sectionIds.length) {
    body.sections = sectionIds.join(',');
    body.sections_url = window.location.pathname;
  }

  const response = await fetch(cartUpdateUrl(Theme.routes.cart_update_url), {
    ...fetchConfig('json', {
      body: JSON.stringify(body),
    }),
    credentials: 'same-origin',
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(`cart attribute update failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Keep reading/applying until line items and the qualifier attribute agree.
 * @param {{ items?: unknown[], attributes?: Record<string, string> } | null | undefined} [cartSnapshot]
 * @returns {Promise<void>}
 */
async function syncUntilAligned(cartSnapshot) {
  const hadSnapshot = Boolean(cartSnapshot?.items);
  let applied = false;
  let snapshot = cartSnapshot;

  for (let attempt = 0; attempt < MAX_ALIGN_ATTEMPTS; attempt += 1) {
    const cart = snapshot?.items ? snapshot : await fetchFreshCart();
    snapshot = null;

    const next = resolveBundleQualifier(cart.items || []);
    const current = cart.attributes?.[BUNDLE_QUALIFIER_ATTRIBUTE] ?? '';

    if (next === current) break;

    const updated = await applyBundleQualifier(cart);
    applied = true;
    const verified = updated || cart;
    const verifiedCurrent = verified.attributes?.[BUNDLE_QUALIFIER_ATTRIBUTE] ?? '';

    if (verifiedCurrent === next) break;

    if (attempt < MAX_ALIGN_ATTEMPTS - 1) {
      await delay(150 * (attempt + 1));
    }
  }

  // Remorph after mutations / attribute writes so PowerX order discounts appear or clear.
  if (applied || hadSnapshot) {
    await refreshCartDiscountUi();
  }
}

/**
 * Align the bundle qualifier with the current cart.
 * Callers always wait for the active sync (including queued follow-ups) so remove
 * remorphs happen after `_nb_bundle_qualifier` is cleared.
 * @param {{ items?: unknown[], attributes?: Record<string, string> } | null | undefined} [cartSnapshot]
 * @returns {Promise<void>}
 */
export async function requestBundleQualifierSync(cartSnapshot) {
  if (cartSnapshot?.items) pendingCartSnapshot = cartSnapshot;
  pendingSync = true;

  if (inFlight) {
    await inFlightDone;
    if (pendingSync) {
      await requestBundleQualifierSync();
    }
    return;
  }

  inFlight = true;
  /** @type {() => void} */
  let release = () => {};
  inFlightDone = new Promise((resolve) => {
    release = resolve;
  });

  try {
    while (pendingSync) {
      pendingSync = false;
      const snapshot = pendingCartSnapshot;
      pendingCartSnapshot = null;
      await syncUntilAligned(snapshot);
    }
  } catch (error) {
    console.warn('bundle-cart-qualifier: sync failed', error);
  } finally {
    inFlight = false;
    release();
  }

  // Process anything queued between the while-exit and inFlight=false.
  if (pendingSync) {
    await requestBundleQualifierSync();
  }
}

function scheduleFollowUpSync() {
  if (followUpTimer) clearTimeout(followUpTimer);
  followUpTimer = setTimeout(() => {
    followUpTimer = null;
    requestBundleQualifierSync();
  }, FOLLOW_UP_MS);
}

/** @param {{ items?: unknown[], attributes?: Record<string, string> } | null | undefined} [cartSnapshot] */
function triggerSync(cartSnapshot) {
  requestBundleQualifierSync(cartSnapshot);
  scheduleFollowUpSync();
}

/** @param {Event} event */
function onCartUpdate(event) {
  if (!(event instanceof CustomEvent) && !(event instanceof CartUpdateEvent)) return;
  if (event.detail?.data?.source === SOURCE) return;

  const resource = event.detail?.resource;
  triggerSync(resource?.items ? resource : undefined);
}

/** @param {Event} event */
function onDialogOpen(event) {
  if (!(event.target instanceof Element)) return;
  if (!event.target.closest('cart-drawer-component')) return;
  triggerSync();
}

document.addEventListener(ThemeEvents.cartUpdate, onCartUpdate);
document.addEventListener('dialog:open', onDialogOpen);
document.addEventListener('DOMContentLoaded', () => triggerSync());
window.addEventListener('pageshow', () => triggerSync());
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') triggerSync();
});
